import React, { useState, useEffect } from 'react'
import { api } from '../../services/api'
import toast from 'react-hot-toast'
import { 
  Plus, 
  Edit, 
  Trash2, 
  Star, 
  StarOff, 
  Eye, 
  FileText,
  ChevronRight,
  ChevronDown,
  X,
  Save
} from 'lucide-react'

const HRAManagement = () => {
  const [questionSets, setQuestionSets] = useState([])
  const [loading, setLoading] = useState(true)
  const [expandedSets, setExpandedSets] = useState(new Set())
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [editingSet, setEditingSet] = useState(null)
  const [editingQuestion, setEditingQuestion] = useState(null)
  const [showQuestionForm, setShowQuestionForm] = useState(false)
  const [selectedSetId, setSelectedSetId] = useState(null)
  const [questionSetDetails, setQuestionSetDetails] = useState({})
  const [activeTab, setActiveTab] = useState('question-sets') // 'question-sets' or 'history'
  const [hraHistory, setHraHistory] = useState([])
  const [loadingHistory, setLoadingHistory] = useState(false)
  const [historyPage, setHistoryPage] = useState(1)
  const [historyTotalPages, setHistoryTotalPages] = useState(1)
  
  const [newQuestionSet, setNewQuestionSet] = useState({
    set_number: '',
    name: '',
    description: '',
    is_active: true,
    is_default: false,
    version: 'v1.0'
  })

  const [newQuestion, setNewQuestion] = useState({
    question_id: '',
    question_text: '',
    question_type: 'single_choice',
    display_order: 0,
    is_required: true,
    recommendation_text: '',
    min_value: null,
    max_value: null,
    scale_labels: null,
    visible_if: null,
    options: []
  })

  useEffect(() => {
    if (activeTab === 'question-sets') {
      loadQuestionSets()
    } else if (activeTab === 'history') {
      loadHRAHistory()
    }
  }, [activeTab, historyPage])

  const loadQuestionSets = async () => {
    try {
      setLoading(true)
      const response = await api.get('/admin/hra/question-sets')
      setQuestionSets(response.data)
    } catch (error) {
      console.error('Error loading question sets:', error)
      toast.error('Failed to load question sets')
    } finally {
      setLoading(false)
    }
  }

  const loadQuestionSetDetails = async (setId) => {
    try {
      const response = await api.get(`/admin/hra/question-sets/${setId}`)
      setQuestionSetDetails(prev => ({
        ...prev,
        [setId]: response.data
      }))
      return response.data
    } catch (error) {
      console.error('Error loading question set details:', error)
      toast.error('Failed to load question set details')
      return null
    }
  }

  const toggleSetExpansion = async (setId) => {
    const newExpanded = new Set(expandedSets)
    if (newExpanded.has(setId)) {
      newExpanded.delete(setId)
    } else {
      newExpanded.add(setId)
      await loadQuestionSetDetails(setId)
    }
    setExpandedSets(newExpanded)
  }

  const handleCreateQuestionSet = async (e) => {
    e.preventDefault()
    try {
      await api.post('/admin/hra/question-sets', newQuestionSet)
      toast.success('Question set created successfully')
      setShowCreateForm(false)
      setNewQuestionSet({
        set_number: '',
        name: '',
        description: '',
        is_active: true,
        is_default: false,
        version: 'v1.0'
      })
      loadQuestionSets()
    } catch (error) {
      console.error('Error creating question set:', error)
      toast.error(error.response?.data?.detail || 'Failed to create question set')
    }
  }

  const handleEditQuestionSet = async (e) => {
    e.preventDefault()
    try {
      await api.put(`/admin/hra/question-sets/${editingSet.id}`, {
        name: editingSet.name,
        description: editingSet.description,
        is_active: editingSet.is_active,
        is_default: editingSet.is_default,
        version: editingSet.version
      })
      toast.success('Question set updated successfully')
      setEditingSet(null)
      loadQuestionSets()
      // Reload details if expanded
      if (expandedSets.has(editingSet.id)) {
        await loadQuestionSetDetails(editingSet.id)
      }
    } catch (error) {
      console.error('Error updating question set:', error)
      toast.error(error.response?.data?.detail || 'Failed to update question set')
    }
  }

  const handleDeleteQuestionSet = async (setId) => {
    if (!window.confirm('Are you sure you want to delete this question set? This will delete all questions in the set.')) {
      return
    }

    try {
      await api.delete(`/admin/hra/question-sets/${setId}`)
      toast.success('Question set deleted successfully')
      loadQuestionSets()
      const newExpanded = new Set(expandedSets)
      newExpanded.delete(setId)
      setExpandedSets(newExpanded)
    } catch (error) {
      console.error('Error deleting question set:', error)
      toast.error('Failed to delete question set')
    }
  }

  const handleSetDefault = async (setId) => {
    try {
      await api.post(`/admin/hra/question-sets/${setId}/set-default`)
      toast.success('Default question set updated')
      loadQuestionSets()
    } catch (error) {
      console.error('Error setting default:', error)
      toast.error('Failed to set default question set')
    }
  }

  const handleToggleActive = async (setId, currentStatus) => {
    try {
      await api.put(`/admin/hra/question-sets/${setId}`, {
        is_active: !currentStatus
      })
      toast.success('Question set status updated')
      loadQuestionSets()
      // Reload details if expanded
      if (expandedSets.has(setId)) {
        await loadQuestionSetDetails(setId)
      }
    } catch (error) {
      console.error('Error updating question set:', error)
      toast.error('Failed to update question set')
    }
  }

  const handleCreateQuestion = async (e) => {
    e.preventDefault()
    try {
      const questionData = {
        ...newQuestion,
        options: newQuestion.options.length > 0 ? newQuestion.options : undefined
      }
      await api.post(`/admin/hra/question-sets/${selectedSetId}/questions`, questionData)
      toast.success('Question created successfully')
      setShowQuestionForm(false)
      setNewQuestion({
        question_id: '',
        question_text: '',
        question_type: 'single_choice',
        display_order: 0,
        is_required: true,
        recommendation_text: '',
        min_value: null,
        max_value: null,
        scale_labels: null,
        visible_if: null,
        options: []
      })
      setSelectedSetId(null)
      await loadQuestionSetDetails(selectedSetId)
      loadQuestionSets()
    } catch (error) {
      console.error('Error creating question:', error)
      toast.error(error.response?.data?.detail || 'Failed to create question')
    }
  }

  const handleUpdateQuestion = async (e) => {
    e.preventDefault()
    try {
      // Update question basic fields
      await api.put(`/admin/hra/questions/${editingQuestion.id}`, {
        question_text: editingQuestion.question_text,
        question_type: editingQuestion.question_type,
        display_order: editingQuestion.display_order,
        is_required: editingQuestion.is_required,
        recommendation_text: editingQuestion.recommendation_text,
        min_value: editingQuestion.min_value,
        max_value: editingQuestion.max_value,
        scale_labels: editingQuestion.scale_labels,
        visible_if: editingQuestion.visible_if
      })

      // Handle options if question type supports them
      if (editingQuestion.question_type === 'single_choice' || editingQuestion.question_type === 'multiple_choice') {
        const existingOptions = editingQuestion.options || []
        const currentOptions = editingQuestion.editingOptions || []
        
        // Get existing option IDs
        const existingOptionIds = new Set(existingOptions.map(opt => opt.id).filter(Boolean))
        const currentOptionIds = new Set(currentOptions.map(opt => opt.id).filter(Boolean))
        
        // Delete options that were removed
        const optionsToDelete = existingOptions.filter(opt => opt.id && !currentOptionIds.has(opt.id))
        for (const option of optionsToDelete) {
          try {
            await api.delete(`/admin/hra/options/${option.id}`)
          } catch (error) {
            console.error(`Error deleting option ${option.id}:`, error)
          }
        }
        
        // Update or create options
        for (const option of currentOptions) {
          if (option.id) {
            // Update existing option
            try {
              await api.put(`/admin/hra/options/${option.id}`, {
                option_value: option.option_value,
                option_label: option.option_label,
                display_order: option.display_order
              })
            } catch (error) {
              console.error(`Error updating option ${option.id}:`, error)
            }
          } else {
            // Create new option
            try {
              await api.post(`/admin/hra/questions/${editingQuestion.id}/options`, {
                option_value: option.option_value,
                option_label: option.option_label,
                display_order: option.display_order
              })
            } catch (error) {
              console.error(`Error creating option:`, error)
            }
          }
        }
      }

      toast.success('Question updated successfully')
      setEditingQuestion(null)
      await loadQuestionSetDetails(editingQuestion.question_set_id)
      loadQuestionSets()
    } catch (error) {
      console.error('Error updating question:', error)
      toast.error(error.response?.data?.detail || 'Failed to update question')
    }
  }

  const handleDeleteQuestion = async (questionId, questionSetId) => {
    if (!window.confirm('Are you sure you want to delete this question? This will also delete all options and scoring rules.')) {
      return
    }

    try {
      await api.delete(`/admin/hra/questions/${questionId}`)
      toast.success('Question deleted successfully')
      await loadQuestionSetDetails(questionSetId)
      loadQuestionSets()
    } catch (error) {
      console.error('Error deleting question:', error)
      toast.error('Failed to delete question')
    }
  }

  const addQuestionOption = () => {
    setNewQuestion({
      ...newQuestion,
      options: [...newQuestion.options, { option_value: '', option_label: '', display_order: newQuestion.options.length }]
    })
  }

  const updateQuestionOption = (index, field, value) => {
    const updatedOptions = [...newQuestion.options]
    updatedOptions[index] = { ...updatedOptions[index], [field]: value }
    setNewQuestion({ ...newQuestion, options: updatedOptions })
  }

  const removeQuestionOption = (index) => {
    const updatedOptions = newQuestion.options.filter((_, i) => i !== index)
    setNewQuestion({ ...newQuestion, options: updatedOptions })
  }

  // Functions for editing question options
  const addEditingQuestionOption = () => {
    const currentOptions = editingQuestion.editingOptions || []
    setEditingQuestion({
      ...editingQuestion,
      editingOptions: [...currentOptions, { option_value: '', option_label: '', display_order: currentOptions.length }]
    })
  }

  const updateEditingQuestionOption = (index, field, value) => {
    const updatedOptions = [...(editingQuestion.editingOptions || [])]
    updatedOptions[index] = { ...updatedOptions[index], [field]: value }
    setEditingQuestion({ ...editingQuestion, editingOptions: updatedOptions })
  }

  const removeEditingQuestionOption = (index) => {
    const updatedOptions = (editingQuestion.editingOptions || []).filter((_, i) => i !== index)
    setEditingQuestion({ ...editingQuestion, editingOptions: updatedOptions })
  }

  const loadHRAHistory = async () => {
    try {
      setLoadingHistory(true)
      const response = await api.get('/admin/hra/history', {
        params: {
          limit: 20,
          offset: (historyPage - 1) * 20
        }
      })
      
      if (response.data.history) {
        setHraHistory(response.data.history)
        setHistoryTotalPages(response.data.total_pages || 1)
      } else {
        setHraHistory(response.data || [])
        setHistoryTotalPages(1)
      }
    } catch (error) {
      console.error('Error loading HRA history:', error)
      toast.error('Failed to load HRA history')
      setHraHistory([])
    } finally {
      setLoadingHistory(false)
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getRiskColor = (riskCategory) => {
    if (!riskCategory) return 'bg-gray-100 text-gray-800'
    switch (riskCategory.toLowerCase()) {
      case 'low': return 'bg-green-100 text-green-800'
      case 'moderate': return 'bg-yellow-100 text-yellow-800'
      case 'high': return 'bg-orange-100 text-orange-800'
      case 'very_high': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getScoreColor = (score) => {
    if (score === null || score === undefined) return 'text-gray-400'
    if (score >= 80) return 'text-green-600'
    if (score >= 60) return 'text-yellow-600'
    return 'text-red-600'
  }

  if (loading && questionSets.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div>
      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('question-sets')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'question-sets'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Question Sets
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'history'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            HRA History
          </button>
        </nav>
      </div>

      {/* Question Sets Tab */}
      {activeTab === 'question-sets' && (
        <>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900">HRA Question Sets</h3>
        <button
          onClick={() => setShowCreateForm(true)}
          className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
        >
          <Plus className="w-4 h-4" />
          <span>Create Question Set</span>
        </button>
      </div>

      {/* Create Question Set Form */}
      {showCreateForm && (
        <div className="bg-gray-50 rounded-lg p-6 mb-6 border border-gray-200">
          <div className="flex justify-between items-center mb-4">
            <h4 className="text-lg font-medium text-gray-900">Create New Question Set</h4>
            <button
              onClick={() => {
                setShowCreateForm(false)
                setNewQuestionSet({
                  set_number: '',
                  name: '',
                  description: '',
                  is_active: true,
                  is_default: false,
                  version: 'v1.0'
                })
              }}
              className="text-gray-500 hover:text-gray-700"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <form onSubmit={handleCreateQuestionSet} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Set Number
                </label>
                <input
                  type="number"
                  value={newQuestionSet.set_number}
                  onChange={(e) => setNewQuestionSet({...newQuestionSet, set_number: parseInt(e.target.value)})}
                  required
                  min="1"
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Version
                </label>
                <input
                  type="text"
                  value={newQuestionSet.version}
                  onChange={(e) => setNewQuestionSet({...newQuestionSet, version: e.target.value})}
                  required
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name
              </label>
              <input
                type="text"
                value={newQuestionSet.name}
                onChange={(e) => setNewQuestionSet({...newQuestionSet, name: e.target.value})}
                required
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={newQuestionSet.description}
                onChange={(e) => setNewQuestionSet({...newQuestionSet, description: e.target.value})}
                rows="3"
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              />
            </div>
            <div className="flex items-center space-x-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={newQuestionSet.is_active}
                  onChange={(e) => setNewQuestionSet({...newQuestionSet, is_active: e.target.checked})}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">Active</span>
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={newQuestionSet.is_default}
                  onChange={(e) => setNewQuestionSet({...newQuestionSet, is_default: e.target.checked})}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">Set as Default</span>
              </label>
            </div>
            <div className="flex space-x-2">
              <button
                type="submit"
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
              >
                Create
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowCreateForm(false)
                  setNewQuestionSet({
                    set_number: '',
                    name: '',
                    description: '',
                    is_active: true,
                    is_default: false,
                    version: 'v1.0'
                  })
                }}
                className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Edit Question Set Modal */}
      {editingSet && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h4 className="text-lg font-medium text-gray-900">Edit Question Set</h4>
              <button
                onClick={() => setEditingSet(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleEditQuestionSet} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Set Number (read-only)
                  </label>
                  <input
                    type="number"
                    value={editingSet.set_number}
                    disabled
                    className="w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Version
                  </label>
                  <input
                    type="text"
                    value={editingSet.version}
                    onChange={(e) => setEditingSet({...editingSet, version: e.target.value})}
                    required
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name
                </label>
                <input
                  type="text"
                  value={editingSet.name}
                  onChange={(e) => setEditingSet({...editingSet, name: e.target.value})}
                  required
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={editingSet.description || ''}
                  onChange={(e) => setEditingSet({...editingSet, description: e.target.value})}
                  rows="3"
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>
              <div className="flex items-center space-x-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={editingSet.is_active}
                    onChange={(e) => setEditingSet({...editingSet, is_active: e.target.checked})}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">Active</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={editingSet.is_default}
                    onChange={(e) => setEditingSet({...editingSet, is_default: e.target.checked})}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">Set as Default</span>
                </label>
              </div>
              <div className="flex space-x-2">
                <button
                  type="submit"
                  className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                >
                  Save Changes
                </button>
                <button
                  type="button"
                  onClick={() => setEditingSet(null)}
                  className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Question Sets List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase w-12"></th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Set #</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Questions</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Default</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {questionSets.map((set) => {
              const isExpanded = expandedSets.has(set.id)
              const details = questionSetDetails[set.id]
              return (
                <React.Fragment key={set.id}>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => toggleSetExpansion(set.id)}
                        className="text-gray-500 hover:text-gray-700"
                      >
                        {isExpanded ? (
                          <ChevronDown className="w-5 h-5" />
                        ) : (
                          <ChevronRight className="w-5 h-5" />
                        )}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {set.set_number}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{set.name}</div>
                      {set.description && (
                        <div className="text-sm text-gray-500">{set.description}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {set.question_count || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        set.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {set.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {set.is_default ? (
                        <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                      ) : (
                        <StarOff className="w-5 h-5 text-gray-400" />
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => setEditingSet({...set})}
                          className="text-blue-600 hover:text-blue-900"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedSetId(set.id)
                            setShowQuestionForm(true)
                          }}
                          className="text-green-600 hover:text-green-900"
                          title="Add Question"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleSetDefault(set.id)}
                          className="text-yellow-600 hover:text-yellow-900"
                          title="Set as default"
                        >
                          <Star className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleToggleActive(set.id, set.is_active)}
                          className="text-purple-600 hover:text-purple-900"
                          title={set.is_active ? 'Deactivate' : 'Activate'}
                        >
                          {set.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                        <button
                          onClick={() => handleDeleteQuestionSet(set.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                  {isExpanded && details && (
                    <tr>
                      <td colSpan="7" className="px-6 py-4 bg-gray-50">
                        <div className="space-y-4">
                          <div className="flex justify-between items-center">
                            <h5 className="text-md font-medium text-gray-900">
                              Questions ({details.questions?.length || 0})
                            </h5>
                            <button
                              onClick={() => {
                                setSelectedSetId(set.id)
                                setShowQuestionForm(true)
                              }}
                              className="flex items-center space-x-1 text-sm bg-green-600 text-white px-3 py-1 rounded-md hover:bg-green-700"
                            >
                              <Plus className="w-4 h-4" />
                              <span>Add Question</span>
                            </button>
                          </div>
                          {details.questions && details.questions.length > 0 ? (
                            <div className="space-y-3">
                              {details.questions
                                .sort((a, b) => a.display_order - b.display_order)
                                .map((question) => (
                                <div key={question.id} className="bg-white border border-gray-200 rounded-lg p-4">
                                  <div className="flex justify-between items-start">
                                    <div className="flex-1">
                                      <div className="flex items-center space-x-2 mb-2">
                                        <span className="text-xs font-medium text-gray-500">
                                          #{question.display_order}
                                        </span>
                                        <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded">
                                          {question.question_type}
                                        </span>
                                        {question.is_required && (
                                          <span className="text-xs px-2 py-1 bg-red-100 text-red-800 rounded">
                                            Required
                                          </span>
                                        )}
                                      </div>
                                      <h6 className="font-medium text-gray-900 mb-1">
                                        {question.question_id}
                                      </h6>
                                      <p className="text-sm text-gray-700 mb-2">
                                        {question.question_text}
                                      </p>
                                      {question.recommendation_text && (
                                        <p className="text-xs text-gray-500 italic mb-2">
                                          Recommendation: {question.recommendation_text}
                                        </p>
                                      )}
                                      {question.options && question.options.length > 0 && (
                                        <div className="mt-2">
                                          <p className="text-xs font-medium text-gray-600 mb-1">Options:</p>
                                          <ul className="list-disc list-inside text-xs text-gray-600 space-y-1">
                                            {question.options
                                              .sort((a, b) => a.display_order - b.display_order)
                                              .map((opt) => (
                                              <li key={opt.id}>
                                                {opt.option_label} ({opt.option_value})
                                              </li>
                                            ))}
                                          </ul>
                                        </div>
                                      )}
                                    </div>
                                    <div className="flex space-x-2 ml-4">
                                      <button
                                        onClick={() => {
                                          // Load question with options for editing
                                          setEditingQuestion({
                                            ...question,
                                            editingOptions: question.options ? question.options.map(opt => ({
                                              id: opt.id,
                                              option_value: opt.option_value,
                                              option_label: opt.option_label,
                                              display_order: opt.display_order
                                            })) : []
                                          })
                                        }}
                                        className="text-blue-600 hover:text-blue-900"
                                        title="Edit"
                                      >
                                        <Edit className="w-4 h-4" />
                                      </button>
                                      <button
                                        onClick={() => handleDeleteQuestion(question.id, question.question_set_id)}
                                        className="text-red-600 hover:text-red-900"
                                        title="Delete"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-center py-8 text-gray-500">
                              No questions in this set. Add one to get started.
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              )
            })}
          </tbody>
        </table>
        {questionSets.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No question sets found. Create one to get started.
          </div>
        )}
      </div>

      {/* Create/Edit Question Modal */}
      {(showQuestionForm || editingQuestion) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h4 className="text-lg font-medium text-gray-900">
                {editingQuestion ? 'Edit Question' : 'Add New Question'}
              </h4>
              <button
                onClick={() => {
                  setShowQuestionForm(false)
                  setEditingQuestion(null)
                  setSelectedSetId(null)
                  setNewQuestion({
                    question_id: '',
                    question_text: '',
                    question_type: 'single_choice',
                    display_order: 0,
                    is_required: true,
                    recommendation_text: '',
                    min_value: null,
                    max_value: null,
                    scale_labels: null,
                    visible_if: null,
                    options: []
                  })
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={editingQuestion ? handleUpdateQuestion : handleCreateQuestion} className="space-y-4">
              {!editingQuestion && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Question ID *
                  </label>
                  <input
                    type="text"
                    value={newQuestion.question_id}
                    onChange={(e) => setNewQuestion({...newQuestion, question_id: e.target.value})}
                    required
                    placeholder="e.g., hra_q1"
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Question Text *
                </label>
                <textarea
                  value={editingQuestion ? editingQuestion.question_text : newQuestion.question_text}
                  onChange={(e) => editingQuestion 
                    ? setEditingQuestion({...editingQuestion, question_text: e.target.value})
                    : setNewQuestion({...newQuestion, question_text: e.target.value})
                  }
                  required
                  rows="3"
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Question Type *
                  </label>
                  <select
                    value={editingQuestion ? editingQuestion.question_type : newQuestion.question_type}
                    onChange={(e) => editingQuestion
                      ? setEditingQuestion({...editingQuestion, question_type: e.target.value})
                      : setNewQuestion({...newQuestion, question_type: e.target.value})
                    }
                    required
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  >
                    <option value="single_choice">Single Choice</option>
                    <option value="multiple_choice">Multiple Choice</option>
                    <option value="yes_no">Yes/No</option>
                    <option value="rating_scale">Rating Scale</option>
                    <option value="number">Number</option>
                    <option value="text">Text</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Display Order
                  </label>
                  <input
                    type="number"
                    value={editingQuestion ? editingQuestion.display_order : newQuestion.display_order}
                    onChange={(e) => editingQuestion
                      ? setEditingQuestion({...editingQuestion, display_order: parseInt(e.target.value) || 0})
                      : setNewQuestion({...newQuestion, display_order: parseInt(e.target.value) || 0})
                    }
                    min="0"
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={editingQuestion ? editingQuestion.is_required : newQuestion.is_required}
                  onChange={(e) => editingQuestion
                    ? setEditingQuestion({...editingQuestion, is_required: e.target.checked})
                    : setNewQuestion({...newQuestion, is_required: e.target.checked})
                  }
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">Required</span>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Recommendation Text
                </label>
                <textarea
                  value={editingQuestion ? (editingQuestion.recommendation_text || '') : (newQuestion.recommendation_text || '')}
                  onChange={(e) => editingQuestion
                    ? setEditingQuestion({...editingQuestion, recommendation_text: e.target.value})
                    : setNewQuestion({...newQuestion, recommendation_text: e.target.value})
                  }
                  rows="2"
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>
              {(editingQuestion?.question_type === 'rating_scale' || newQuestion.question_type === 'rating_scale') && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Min Value
                    </label>
                    <input
                      type="number"
                      value={editingQuestion ? (editingQuestion.min_value || '') : (newQuestion.min_value || '')}
                      onChange={(e) => editingQuestion
                        ? setEditingQuestion({...editingQuestion, min_value: e.target.value ? parseInt(e.target.value) : null})
                        : setNewQuestion({...newQuestion, min_value: e.target.value ? parseInt(e.target.value) : null})
                      }
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Max Value
                    </label>
                    <input
                      type="number"
                      value={editingQuestion ? (editingQuestion.max_value || '') : (newQuestion.max_value || '')}
                      onChange={(e) => editingQuestion
                        ? setEditingQuestion({...editingQuestion, max_value: e.target.value ? parseInt(e.target.value) : null})
                        : setNewQuestion({...newQuestion, max_value: e.target.value ? parseInt(e.target.value) : null})
                      }
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                    />
                  </div>
                </div>
              )}
              {(editingQuestion?.question_type === 'single_choice' || editingQuestion?.question_type === 'multiple_choice' || 
                (!editingQuestion && (newQuestion.question_type === 'single_choice' || newQuestion.question_type === 'multiple_choice'))) && (
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Options
                    </label>
                    <button
                      type="button"
                      onClick={editingQuestion ? addEditingQuestionOption : addQuestionOption}
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      + Add Option
                    </button>
                  </div>
                  {(editingQuestion ? (editingQuestion.editingOptions || []) : newQuestion.options).map((option, index) => (
                    <div key={index} className="flex space-x-2 mb-2">
                      <input
                        type="text"
                        placeholder="Option Value"
                        value={option.option_value}
                        onChange={(e) => editingQuestion 
                          ? updateEditingQuestionOption(index, 'option_value', e.target.value)
                          : updateQuestionOption(index, 'option_value', e.target.value)
                        }
                        className="flex-1 border border-gray-300 rounded-md px-3 py-2"
                        required
                      />
                      <input
                        type="text"
                        placeholder="Option Label"
                        value={option.option_label}
                        onChange={(e) => editingQuestion
                          ? updateEditingQuestionOption(index, 'option_label', e.target.value)
                          : updateQuestionOption(index, 'option_label', e.target.value)
                        }
                        className="flex-1 border border-gray-300 rounded-md px-3 py-2"
                        required
                      />
                      <input
                        type="number"
                        placeholder="Order"
                        value={option.display_order}
                        onChange={(e) => editingQuestion
                          ? updateEditingQuestionOption(index, 'display_order', parseInt(e.target.value) || 0)
                          : updateQuestionOption(index, 'display_order', parseInt(e.target.value) || 0)
                        }
                        className="w-20 border border-gray-300 rounded-md px-3 py-2"
                      />
                      <button
                        type="button"
                        onClick={() => editingQuestion
                          ? removeEditingQuestionOption(index)
                          : removeQuestionOption(index)
                        }
                        className="text-red-600 hover:text-red-800"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex space-x-2">
                <button
                  type="submit"
                  className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                >
                  {editingQuestion ? 'Save Changes' : 'Create Question'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowQuestionForm(false)
                    setEditingQuestion(null)
                    setSelectedSetId(null)
                    setNewQuestion({
                      question_id: '',
                      question_text: '',
                      question_type: 'single_choice',
                      display_order: 0,
                      is_required: true,
                      recommendation_text: '',
                      min_value: null,
                      max_value: null,
                      scale_labels: null,
                      visible_if: null,
                      options: []
                    })
                  }}
                  className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
        </>
      )}

      {/* HRA History Tab */}
      {activeTab === 'history' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">HRA Assessment History</h3>
          </div>

          {loadingHistory ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : hraHistory && hraHistory.length > 0 ? (
            <>
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Completed Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Risk Score</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Risk Category</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Questionnaire Version</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {hraHistory.map((assessment) => (
                      <tr key={assessment.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{assessment.user_name || 'Unknown'}</div>
                            <div className="text-sm text-gray-500">{assessment.user_phone || 'N/A'}</div>
                            {assessment.user_age && (
                              <div className="text-xs text-gray-400">Age: {assessment.user_age}, {assessment.user_gender || 'N/A'}</div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatDate(assessment.completed_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {assessment.risk_assessment?.overall_risk_score !== null && assessment.risk_assessment?.overall_risk_score !== undefined ? (
                            <div className={`text-lg font-semibold ${getScoreColor(assessment.risk_assessment.overall_risk_score)}`}>
                              {assessment.risk_assessment.overall_risk_score}
                            </div>
                          ) : (
                            <span className="text-gray-400">N/A</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {assessment.risk_assessment?.risk_category ? (
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRiskColor(assessment.risk_assessment.risk_category)}`}>
                              {assessment.risk_assessment.risk_category.toUpperCase()}
                            </span>
                          ) : (
                            <span className="text-gray-400">N/A</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {assessment.questionnaire_version || 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {historyTotalPages > 1 && (
                <div className="flex items-center justify-between mt-6">
                  <div className="text-sm text-gray-700">
                    Showing page {historyPage} of {historyTotalPages}
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setHistoryPage(Math.max(1, historyPage - 1))}
                      disabled={historyPage === 1}
                      className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setHistoryPage(Math.min(historyTotalPages, historyPage + 1))}
                      disabled={historyPage === historyTotalPages}
                      className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="bg-white rounded-lg shadow p-12 text-center">
              <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                No HRA Assessments Yet
              </h3>
              <p className="text-gray-600">
                No health risk assessments have been completed yet.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default HRAManagement
